import { useCallback, useEffect, useMemo, useRef, useState, type SVGProps } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Skeleton, SkeletonText } from '@/components/ui/Skeleton'
import {
  fetchSubscription,
  startBillingSetup,
  syncBillingPayment,
  type MerchantSubscription,
} from '@/services/billingApi'
import { useAuth } from '@/context/AuthContext'
import { useCurrentStore } from '@/context/CurrentStoreContext'
import { cn } from '@/lib/utils'

function IconSpinner(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" opacity="0.25" />
      <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function IconCard(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <rect x="3" y="6" width="18" height="12" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M3 10h18" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
}

function IconCheck(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="m8 12 2.5 2.5L16 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function IconClock(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

function IconShield(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <path d="M12 3 5 6v6c0 4 3 7 7 9 4-2 7-5 7-9V6l-7-3Z" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
}

function IconSpark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <path d="M12 3l1.8 5.5L19 10l-5.2 1.5L12 17l-1.8-5.5L5 10l5.2-1.5L12 3Z" stroke="currentColor" strokeWidth="2" />
    </svg>
  )
}

function IconRefresh(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden {...props}>
      <path d="M20 12a8 8 0 1 1-2.3-5.7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M20 4v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  )
}

const STATUS_LABELS: Record<string, string> = {
  pending_mandate: 'Payment method required',
  trialing: 'Free trial active',
  active: 'Active',
  past_due: 'Payment overdue',
  expired: 'Expired',
  cancelled: 'Cancelled',
}

const STATUS_TONE: Record<string, string> = {
  pending_mandate: 'bg-amber-500/15 text-amber-200 ring-amber-500/30',
  trialing: 'bg-emerald-500/15 text-emerald-200 ring-emerald-500/30',
  active: 'bg-emerald-500/15 text-emerald-200 ring-emerald-500/30',
  past_due: 'bg-red-500/15 text-red-200 ring-red-500/30',
  expired: 'bg-red-500/15 text-red-200 ring-red-500/30',
  cancelled: 'bg-muted text-muted-foreground ring-border',
}

function formatDate(value: string | null | undefined) {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function StatusBadge({ status }: { status: string }) {
  const label = STATUS_LABELS[status] ?? status.replace(/_/g, ' ')
  const tone = STATUS_TONE[status] ?? STATUS_TONE.cancelled
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
        tone,
      )}
    >
      {label}
    </span>
  )
}

function PaymentMethodCard({
  subscription,
  canManage,
  updating,
  onUpdate,
}: {
  subscription: MerchantSubscription | null
  canManage: boolean
  updating: boolean
  onUpdate: () => void
}) {
  const pm = subscription?.paymentMethod
  const hasCard = pm?.present

  return (
    <div className="rounded-xl border border-border/70 bg-background/60 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Payment method
          </p>
          {hasCard ? (
            <>
              <p className="mt-1 font-medium text-foreground">
                {pm?.label ?? 'Card on file'}
              </p>
              {pm?.cardExpiry ? (
                <p className="mt-0.5 text-sm text-muted-foreground">Expires {pm.cardExpiry}</p>
              ) : pm?.consumerAccount ? (
                <p className="mt-0.5 text-sm text-muted-foreground">{pm.consumerAccount}</p>
              ) : null}
            </>
          ) : (
            <p className="mt-1 text-sm text-muted-foreground">
              {subscription?.complimentary && subscription?.billingRequired
                ? 'Complimentary access has ended — add a card to continue'
                : subscription?.billingRequired
                  ? 'Add a card to activate your subscription'
                  : 'No payment method on file'}
            </p>
          )}
        </div>
        <IconCard className="h-8 w-8 shrink-0 text-muted-foreground/60" />
      </div>
      {canManage && hasCard ? (
        <div className="mt-4 border-t border-border/60 pt-4">
          <Button type="button" variant="secondary" onClick={onUpdate} disabled={updating}>
            {updating ? 'Redirecting…' : 'Update payment method'}
          </Button>
          <p className="mt-2 text-xs text-muted-foreground">
            You&apos;ll verify a new card with{' '}
            {subscription?.billingProvider === 'paystack' ||
            subscription?.pendingBillingProvider === 'paystack'
              ? 'Paystack'
              : 'Stripe'}
            . Your plan and billing dates stay the same.
          </p>
        </div>
      ) : null}
    </div>
  )
}

function BillingDetails({
  subscription,
  syncing,
  syncMessage,
  isReturn,
  paymentMethodUpdated,
}: {
  subscription: MerchantSubscription | null
  syncing?: boolean
  syncMessage?: string | null
  isReturn?: boolean
  paymentMethodUpdated?: boolean
}) {
  const trialEnds = formatDate(subscription?.trialEndsAt)
  const periodEnds = formatDate(subscription?.currentPeriodEndsAt)
  const graceEnds = formatDate(subscription?.graceEndsAt)

  return (
    <div className="space-y-5">
      {paymentMethodUpdated ? (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
          <IconCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">Payment method updated</p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              Your new card is saved for future subscription renewals.
            </p>
          </div>
        </div>
      ) : null}
      {isReturn && subscription?.billingRequired ? (
        <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
          {syncing ? (
            <IconSpinner className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-primary" />
          ) : (
            <IconClock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          )}
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              {syncing
                ? `Confirming your payment with ${
                    subscription?.billingProvider === 'paystack' ||
                    subscription?.pendingBillingProvider === 'paystack'
                      ? 'Paystack'
                      : 'Stripe'
                  }…`
                : 'Waiting for payment confirmation'}
            </p>
            <p className="text-xs leading-relaxed text-muted-foreground">
              This usually takes a few seconds. We&apos;ll clear the billing reminder automatically once
              your payment method is verified.
            </p>
            {syncMessage ? (
              <p className="text-xs text-muted-foreground">{syncMessage}</p>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="rounded-xl border border-border/70 bg-background/60 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Current plan
            </p>
            <p className="mt-1 font-display text-2xl tracking-tight text-foreground">
              {subscription?.planName ?? subscription?.planCode ?? '—'}
            </p>
            {subscription?.displayPrice ? (
              <p className="mt-1 text-sm text-muted-foreground">
                {subscription.displayPrice}
                <span className="text-muted-foreground/80">
                  {subscription.billingInterval === 'yearly' ? ' / year' : ' / month'}
                </span>
              </p>
            ) : null}
          </div>
          {subscription?.status ? <StatusBadge status={subscription.status} /> : null}
        </div>
      </div>

      <dl className="grid gap-3 text-sm">
        {subscription?.eligibleTrial && subscription.status === 'pending_mandate' ? (
          <DetailRow
            icon={IconSpark}
            label="After you add a card"
            value="Your free trial starts immediately"
          />
        ) : null}
        {trialEnds ? <DetailRow icon={IconClock} label="Trial ends" value={trialEnds} /> : null}
        {subscription?.billingInterval ? (
          <DetailRow
            icon={IconCard}
            label="Billing cycle"
            value={subscription.billingInterval === 'yearly' ? 'Yearly' : 'Monthly'}
          />
        ) : null}
        {subscription?.billingLocked && subscription.billingCurrency ? (
          <DetailRow
            icon={IconCard}
            label="Billing currency"
            value={`${subscription.billingCurrency} via ${
              subscription.billingProvider === 'paystack' ? 'Paystack' : 'Stripe'
            } (locked after first payment)`}
          />
        ) : subscription?.pendingBillingCurrency ? (
          <DetailRow
            icon={IconCard}
            label="You will be charged in"
            value={`${subscription.pendingBillingCurrency} (${
              subscription.pendingBillingProvider === 'paystack' ? 'Paystack' : 'Stripe'
            })`}
          />
        ) : null}
        {subscription?.maxStaffCount != null ? (
          <DetailRow
            icon={IconShield}
            label="Staff seats"
            value={`${subscription.staffCount} of ${subscription.maxStaffCount} used${
              subscription.staffSeatsRemaining != null
                ? ` (${subscription.staffSeatsRemaining} remaining)`
                : ''
            }`}
          />
        ) : subscription?.maxStaffCount === null && subscription?.staffCount != null ? (
          <DetailRow
            icon={IconShield}
            label="Staff seats"
            value={`${subscription.staffCount} staff (unlimited on your plan)`}
          />
        ) : null}
        {periodEnds && subscription?.status !== 'pending_mandate' ? (
          <DetailRow icon={IconCard} label="Next billing date" value={periodEnds} />
        ) : null}
        {graceEnds && (subscription?.status === 'past_due' || subscription?.status === 'expired') ? (
          <DetailRow icon={IconClock} label="Grace period ends" value={graceEnds} />
        ) : null}
        {subscription?.status === 'pending_mandate' ? (
          <DetailRow
            icon={IconShield}
            label={
              subscription.pendingBillingProvider === 'paystack'
                ? 'Why Paystack?'
                : 'Why Stripe?'
            }
            value="Secure card verification — we only charge when your trial ends or plan renews"
          />
        ) : null}
      </dl>
    </div>
  )
}

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof IconCard
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border/50 px-3 py-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">{label}</dt>
        <dd className="mt-0.5 font-medium text-foreground">{value}</dd>
      </div>
    </div>
  )
}

function BillingSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading billing">
      <Skeleton className="h-24 w-full rounded-xl" />
      <SkeletonText lines={3} />
      <Skeleton className="h-10 w-44 rounded-md" />
    </div>
  )
}

function useBillingSubscription(options: { isReturn: boolean; autoPoll: boolean }) {
  const { refreshBillingStatus, user } = useAuth()
  const { refreshStores } = useCurrentStore()
  const navigate = useNavigate()
  const [subscription, setSubscription] = useState<MerchantSubscription | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [updatingPayment, setUpdatingPayment] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [paymentMethodUpdated, setPaymentMethodUpdated] = useState(false)
  const pollRef = useRef(0)
  const canManageBilling = user?.role === 'org_admin'

  const refreshBillingStatusRef = useRef(refreshBillingStatus)
  const refreshStoresRef = useRef(refreshStores)
  const navigateRef = useRef(navigate)
  refreshBillingStatusRef.current = refreshBillingStatus
  refreshStoresRef.current = refreshStores
  navigateRef.current = navigate

  const syncSessionBillingFlags = useCallback(async () => {
    await refreshStoresRef.current()
    await refreshBillingStatusRef.current()
  }, [])

  const applyBillingSideEffects = useCallback(
    async (
      sub: MerchantSubscription | null,
      message: string | null | undefined,
      wasBillingRequired: boolean,
    ) => {
      if (message === 'Payment method updated') {
        setPaymentMethodUpdated(true)
        await syncSessionBillingFlags()
        return true
      }
      if (sub && !sub.billingRequired) {
        await syncSessionBillingFlags()
        if (options.isReturn && wasBillingRequired) {
          navigateRef.current('/', { replace: true })
        }
        return true
      }
      return false
    },
    [options.isReturn, syncSessionBillingFlags],
  )

  const load = useCallback(async () => {
    setError(null)
    try {
      const data = await fetchSubscription()
      setSubscription(data)
      if (data && !data.billingRequired) {
        await syncSessionBillingFlags()
      }
      return data
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load billing')
      return null
    }
  }, [syncSessionBillingFlags])

  const sync = useCallback(async () => {
    const wasBillingRequired = Boolean(user?.billingRequired)
    setSyncing(true)
    setSyncMessage(null)
    try {
      const result = await syncBillingPayment()
      setSubscription(result.subscription)
      setSyncMessage(result.message)
      const done = await applyBillingSideEffects(
        result.subscription,
        result.message,
        wasBillingRequired,
      )
      return done
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not sync payment status')
      return false
    } finally {
      setSyncing(false)
    }
  }, [applyBillingSideEffects, user?.billingRequired])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        if (options.isReturn) {
          setSyncing(true)
          const result = await syncBillingPayment()
          if (cancelled) return
          setSubscription(result.subscription)
          setSyncMessage(result.message)
          const wasBillingRequired = Boolean(user?.billingRequired)
          await applyBillingSideEffects(
            result.subscription,
            result.message,
            wasBillingRequired,
          )
        } else {
          const data = await fetchSubscription()
          if (!cancelled) setSubscription(data)
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load billing')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
          setSyncing(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [applyBillingSideEffects, options.isReturn, user?.billingRequired])

  useEffect(() => {
    if (!options.autoPoll || !subscription?.billingRequired) return undefined

    const interval = window.setInterval(() => {
      pollRef.current += 1
      if (pollRef.current > 20) {
        window.clearInterval(interval)
        return
      }
      void sync()
    }, 3000)

    return () => window.clearInterval(interval)
  }, [options.autoPoll, subscription?.billingRequired, sync])

  async function handleSetup() {
    setSubmitting(true)
    setError(null)
    setPaymentMethodUpdated(false)
    try {
      const { authorizationUrl } = await startBillingSetup()
      window.location.href = authorizationUrl
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start billing setup')
      setSubmitting(false)
    }
  }

  async function handleUpdatePaymentMethod() {
    setUpdatingPayment(true)
    setError(null)
    setPaymentMethodUpdated(false)
    try {
      const { authorizationUrl } = await startBillingSetup({ updatePaymentMethod: true })
      window.location.href = authorizationUrl
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start payment method update')
      setUpdatingPayment(false)
    }
  }

  return {
    subscription,
    loading,
    submitting,
    updatingPayment,
    syncing,
    error,
    syncMessage,
    paymentMethodUpdated,
    canManageBilling,
    handleSetup,
    handleUpdatePaymentMethod,
    reload: load,
    sync,
  }
}

export function BillingGateModal() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const {
    subscription,
    loading,
    submitting,
    syncing,
    error,
    syncMessage,
    handleSetup,
    sync,
  } = useBillingSubscription({ isReturn: false, autoPoll: true })

  const pending = subscription?.status === 'pending_mandate'
  const expired = subscription?.status === 'expired' || subscription?.status === 'past_due'

  return (
    <div className="absolute inset-0 z-30 flex items-center justify-center bg-background/70 p-4 backdrop-blur-sm">
      <Card className="w-full max-w-lg overflow-hidden p-0 shadow-lg">
        <div className="border-b border-border/60 bg-gradient-to-br from-primary/10 via-transparent to-transparent px-6 py-5">
          <CardHeader
            title={
              subscription?.complimentary && expired
                ? 'Complimentary access ended'
                : pending
                  ? 'Add a payment method'
                  : 'Subscription required'
            }
            description={
              subscription?.complimentary && expired
                ? 'Your complimentary access has ended. Add a card to continue, or wait if Kartly extends your access. Completing setup charges your plan and moves the store to paid.'
                : pending
                  ? 'Verify your card with Stripe to start your trial. You can keep setting up your store from the dashboard banner if you close this.'
                  : 'Your subscription has expired or is past due. Restore billing to unlock store admin again.'
            }
          />
        </div>
        <div className="space-y-4 p-6">
          {loading ? (
            <BillingSkeleton />
          ) : (
            <>
              <BillingDetails subscription={subscription} syncing={syncing} syncMessage={syncMessage} />
              {error ? (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-3">
                <Button type="button" onClick={() => void handleSetup()} disabled={submitting || syncing}>
                  {submitting
                    ? 'Redirecting…'
                    : subscription?.pendingBillingProvider === 'paystack' ||
                        subscription?.billingProvider === 'paystack'
                      ? 'Continue with Paystack'
                      : 'Continue with Stripe'}
                </Button>
                <Button type="button" variant="secondary" onClick={() => void sync()} disabled={syncing}>
                  <IconRefresh className={cn('mr-2 h-4 w-4', syncing && 'animate-spin')} />
                  Check status
                </Button>
                {expired ? (
                  <Button type="button" variant="secondary" onClick={() => navigate('/billing')}>
                    Billing details
                  </Button>
                ) : null}
              </div>
              {user?.email ? (
                <p className="text-xs text-muted-foreground">Signed in as {user.email}</p>
              ) : null}
            </>
          )}
        </div>
      </Card>
    </div>
  )
}

export default function BillingPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const isReturn = location.pathname.endsWith('/return')
  const {
    subscription,
    loading,
    submitting,
    updatingPayment,
    syncing,
    error,
    syncMessage,
    paymentMethodUpdated,
    canManageBilling,
    handleSetup,
    handleUpdatePaymentMethod,
    sync,
    reload,
  } = useBillingSubscription({ isReturn, autoPoll: isReturn })

  useEffect(() => {
    if (loading || !isReturn || subscription?.billingRequired) return
    navigate('/', { replace: true })
  }, [isReturn, loading, navigate, subscription?.billingRequired])

  const headline = useMemo(() => {
    if (isReturn && subscription?.billingRequired) return 'Finishing setup'
    if (subscription?.complimentary && subscription?.billingRequired) {
      return 'Complimentary access ended'
    }
    if (subscription?.complimentary && subscription?.status === 'trialing') {
      return 'Complimentary access'
    }
    if (subscription?.status === 'trialing') return 'Your trial is active'
    if (subscription?.status === 'active') return 'Subscription active'
    if (subscription?.billingRequired) return 'Complete billing setup'
    return 'Billing & plan'
  }, [isReturn, subscription?.billingRequired, subscription?.complimentary, subscription?.status])

  const providerLabel =
    subscription?.billingProvider === 'paystack' ||
    subscription?.pendingBillingProvider === 'paystack'
      ? 'Paystack'
      : 'Stripe'

  const description = useMemo(() => {
    if (isReturn && subscription?.billingRequired) {
      return `We are confirming your ${providerLabel} payment and activating your Kartly subscription.`
    }
    if (subscription?.complimentary && subscription?.billingRequired) {
      return 'Your complimentary access has ended. Add a payment card to continue, or wait if Kartly extends your access.'
    }
    if (subscription?.complimentary && subscription?.status === 'trialing') {
      return 'This store is on complimentary access. No card is required unless that access ends.'
    }
    if (subscription?.status === 'trialing') {
      return 'Explore your dashboard while your trial runs. Billing starts when the trial ends.'
    }
    return 'Manage your Kartly subscription, trial, and payment method.'
  }, [
    isReturn,
    providerLabel,
    subscription?.billingRequired,
    subscription?.complimentary,
    subscription?.status,
  ])

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="space-y-2">
        <h1 className="font-display text-3xl tracking-tight text-foreground">{headline}</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">{description}</p>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="border-b border-border/60 bg-gradient-to-br from-primary/10 via-transparent to-transparent px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/15 text-primary">
              {subscription?.billingRequired ? (
                <IconCard className="h-5 w-5" />
              ) : (
                <IconCheck className="h-5 w-5" />
              )}
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Kartly subscription</p>
              <p className="text-xs text-muted-foreground">
                {subscription?.planName ?? 'Your plan'} · billed{' '}
                {subscription?.billingInterval === 'yearly' ? 'yearly' : 'monthly'} via {providerLabel}
                {subscription?.pendingBillingCurrency || subscription?.billingCurrency
                  ? ` · ${subscription.billingCurrency ?? subscription.pendingBillingCurrency}`
                  : ''}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-5 p-6">
          {loading ? (
            <BillingSkeleton />
          ) : (
            <>
              <BillingDetails
                subscription={subscription}
                syncing={syncing}
                syncMessage={syncMessage}
                isReturn={isReturn}
                paymentMethodUpdated={paymentMethodUpdated}
              />

              <PaymentMethodCard
                subscription={subscription}
                canManage={canManageBilling}
                updating={updatingPayment}
                onUpdate={() => void handleUpdatePaymentMethod()}
              />

              {error ? (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              ) : null}

              <div className="flex flex-wrap gap-3 border-t border-border/60 pt-4">
                {subscription?.billingRequired ? (
                  canManageBilling ? (
                    <>
                      <Button type="button" onClick={() => void handleSetup()} disabled={submitting || syncing}>
                        {submitting ? 'Redirecting…' : `Continue with ${providerLabel}`}
                      </Button>
                      <Button type="button" variant="secondary" onClick={() => void sync()} disabled={syncing}>
                        <IconRefresh className={cn('mr-2 h-4 w-4', syncing && 'animate-spin')} />
                        {syncing ? 'Checking…' : 'Check payment status'}
                      </Button>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Ask your store owner to complete subscription setup.
                    </p>
                  )
                ) : (
                  <>
                    <Button type="button" onClick={() => navigate('/')}>
                      Go to dashboard
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => void reload()}>
                      Refresh
                    </Button>
                    {subscription?.status === 'trialing' ? (
                      <p className="self-center text-xs text-muted-foreground">
                        No charge until your trial ends.
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  )
}
